import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Settings,
  Timer,
} from "lucide-react";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useTaxCalendar } from "@/lib/api/taxes.queries";
import type { TaxCalendarEvent } from "@/lib/api/taxes";
import { useActiveCompany } from "@/lib/company-context";
import { cn } from "@/lib/cn";

import { TaxEventBadges } from "@/components/taxes/TaxEventBadges";

export const Route = createFileRoute("/companies/$companyId/impuestos/")({
  component: ImpuestosPage,
});

const MONTHS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function currentFiscalYear() {
  return String(new Date().getFullYear());
}

function fiscalYearRange(year: string) {
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

function fiscalYearOptions() {
  const current = new Date().getFullYear();
  return Array.from({ length: 7 }, (_, index) => String(current - 3 + index));
}

function ImpuestosPage() {
  return (
    <RequireAuth>
      <AppShell>
        <ImpuestosContent />
      </AppShell>
    </RequireAuth>
  );
}

function ImpuestosContent() {
  const fmtCop = useMoneyFormatter();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const yearOptions = useMemo(fiscalYearOptions, []);
  const [fiscalYear, setFiscalYear] = useState(currentFiscalYear);
  const range = useMemo(() => fiscalYearRange(fiscalYear), [fiscalYear]);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const calendar = useTaxCalendar(companyId, range);
  const events = useMemo(
    () => calendar.data?.events ?? [],
    [calendar.data?.events],
  );
  const eventsByMonth = useMemo(() => groupEventsByMonth(events), [events]);
  const nextEvents = useMemo(
    () =>
      [...events]
        .filter(
          (event) =>
            event.status !== "paid" && event.status !== "not_applicable",
        )
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
        .slice(0, 5),
    [events],
  );

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            Volver a la empresa
          </Link>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">Impuestos</h1>
          </div>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Calendario anual de vencimientos tributarios por vigencia fiscal,
            con valores proyectados, estado de pago y explicacion por evento.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="grid gap-1.5">
            <label
              className="text-xs font-medium text-muted-foreground"
              htmlFor="tax-fiscal-year"
            >
              Vigencia fiscal
            </label>
            <Select value={fiscalYear} onValueChange={setFiscalYear}>
              <SelectTrigger id="tax-fiscal-year" className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button asChild variant="outline">
            <Link
              to="/companies/$companyId/impuestos/configuracion"
              params={{ companyId }}
            >
              <Settings className="h-4 w-4" />
              Configuracion
            </Link>
          </Button>
        </div>
      </header>

      <Alert variant="info">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Fechas proyectadas</AlertTitle>
        <AlertDescription>
          Esta vista muestra eventos con vencimiento entre {range.from} y{" "}
          {range.to}. Algunas obligaciones corresponden al periodo fiscal
          anterior, pero se pagan en la vigencia seleccionada. IVA (300) y
          retencion en la fuente (350) de 2026 siguen el calendario oficial DIAN
          por ultimo digito del NIT; el contador debe confirmar las demas fechas
          DIAN o municipales antes de presentar y pagar.
        </AlertDescription>
      </Alert>

      <section className="grid gap-3 md:grid-cols-4">
        {calendar.isPending ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))
        ) : calendar.data ? (
          <>
            <SummaryCard
              title="Proyectado"
              value={fmtCop(calendar.data.summary.totalProjected)}
              icon={<CalendarDays className="h-4 w-4 text-primary" />}
            />
            <SummaryCard
              title="Declarado"
              value={fmtCop(calendar.data.summary.totalDeclared)}
              icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            />
            <SummaryCard
              title="Proximos"
              value={String(calendar.data.summary.dueSoonCount)}
              icon={<Timer className="h-4 w-4 text-amber-600" />}
            />
            <SummaryCard
              title="Vencidos"
              value={String(calendar.data.summary.overdueCount)}
              icon={<AlertCircle className="h-4 w-4 text-destructive" />}
            />
          </>
        ) : null}
      </section>

      {calendar.isPending ? (
        <YearCalendarSkeleton />
      ) : calendar.isError ? (
        <ErrorState
          title="No pudimos cargar el calendario tributario"
          description={calendar.error?.message ?? ""}
          onRetry={() => void calendar.refetch()}
          retryLabel="Reintentar"
        />
      ) : events.length ? (
        <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
          <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {MONTHS.map((month, index) => (
              <MonthCard
                key={month}
                companyId={companyId}
                name={month}
                monthNumber={index + 1}
                events={eventsByMonth[index] ?? []}
              />
            ))}
          </section>
          <aside className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Proximos vencimientos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {nextEvents.length ? (
                  nextEvents.map((event) => (
                    <Link
                      key={event.id}
                      to="/companies/$companyId/impuestos/$eventId"
                      params={{ companyId, eventId: event.id }}
                      className="block rounded-md border p-3 transition-colors hover:border-primary/40 hover:bg-accent/40"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium">
                            {event.displayName}
                          </p>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {formatDayMonth(event.dueDate)} ·{" "}
                            {event.period.label}
                          </p>
                        </div>
                        <TaxEventBadges event={event} />
                      </div>
                      <p className="mt-2 text-sm font-semibold">
                        {fmtCop(event.payableAmount)}
                      </p>
                    </Link>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No hay vencimientos pendientes en esta vigencia.
                  </p>
                )}
              </CardContent>
            </Card>
          </aside>
        </div>
      ) : (
        <div className="rounded-lg border bg-card p-10 text-center">
          <p className="font-medium">No hay eventos en esta vigencia</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Activa obligaciones en configuracion o cambia el ano fiscal.
          </p>
        </div>
      )}
    </div>
  );
}

function groupEventsByMonth(events: TaxCalendarEvent[]): TaxCalendarEvent[][] {
  const grouped: TaxCalendarEvent[][] = Array.from({ length: 12 }, () => []);
  for (const event of events) {
    const monthIndex = Number(event.dueDate.slice(5, 7)) - 1;
    if (monthIndex >= 0 && monthIndex <= 11) grouped[monthIndex].push(event);
  }
  for (const month of grouped) {
    month.sort((a, b) =>
      a.dueDate === b.dueDate
        ? a.displayName.localeCompare(b.displayName)
        : a.dueDate.localeCompare(b.dueDate),
    );
  }
  return grouped;
}

function formatDay(iso: string): string {
  return String(Number(iso.slice(8, 10))).padStart(2, "0");
}

function formatDayMonth(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  return date.toLocaleDateString("es-CO", {
    day: "numeric",
    month: "short",
  });
}

function monthTotal(events: TaxCalendarEvent[]): number {
  return events.reduce((sum, event) => sum + event.payableAmount, 0);
}

function MonthCard(props: {
  companyId: string;
  name: string;
  monthNumber: number;
  events: TaxCalendarEvent[];
}) {
  const fmtCop = useMoneyFormatter();
  const hasRisk = props.events.some((event) => event.risk === "overdue");
  const dueSoon = props.events.some((event) => event.risk === "due_soon");
  return (
    <Card
      className={cn(
        "min-h-72 overflow-hidden",
        hasRisk
          ? "border-destructive/40"
          : dueSoon
            ? "border-amber-500/40"
            : undefined,
      )}
    >
      <CardHeader className="space-y-3 border-b bg-muted/30 pb-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">{props.name}</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              {props.events.length} evento{props.events.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-md border bg-background text-sm font-semibold">
            {String(props.monthNumber).padStart(2, "0")}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 text-xs">
          <span className="text-muted-foreground">Proyectado del mes</span>
          <span className="font-semibold">
            {fmtCop(monthTotal(props.events))}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 p-3">
        {props.events.length ? (
          props.events.map((event) => (
            <Link
              key={event.id}
              to="/companies/$companyId/impuestos/$eventId"
              params={{ companyId: props.companyId, eventId: event.id }}
              className={cn(
                "block rounded-md border p-3 transition-colors hover:border-primary/40 hover:bg-accent/40",
                event.status === "paid" &&
                  "bg-emerald-50/60 dark:bg-emerald-950/20",
                event.risk === "overdue" &&
                  "border-destructive/40 bg-destructive/5",
                event.risk === "due_soon" &&
                  "border-amber-500/40 bg-amber-50/60 dark:bg-amber-950/20",
              )}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 flex-col items-center justify-center rounded-md bg-background ring-1 ring-border">
                  <span className="text-[10px] uppercase text-muted-foreground">
                    Dia
                  </span>
                  <span className="text-sm font-semibold">
                    {formatDay(event.dueDate)}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-medium">
                      {event.displayName}
                    </p>
                    {event.formCode ? (
                      <Badge variant="outline" className="shrink-0">
                        {event.formCode}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Periodo {event.period.label}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-semibold">
                      {fmtCop(event.payableAmount)}
                    </span>
                    <TaxEventBadges event={event} />
                  </div>
                </div>
              </div>
            </Link>
          ))
        ) : (
          <div className="flex min-h-40 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
            Sin vencimientos
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function YearCalendarSkeleton() {
  return (
    <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
      {Array.from({ length: 12 }).map((_, index) => (
        <Skeleton key={index} className="h-72 rounded-lg" />
      ))}
    </section>
  );
}

function SummaryCard(props: {
  title: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-muted-foreground">
          {props.title}
        </p>
        {props.icon}
      </div>
      <p className="mt-2 text-xl font-semibold">{props.value}</p>
    </div>
  );
}
